/**
 * Pure helpers for html_css_workspace section div-id data bindings.
 * No React/network dependencies — see HtmlCssSection.js for the component
 * that wires these into useQueries().
 */

/**
 * Translates one binding's { limit, sort, filter } into query params.
 *
 * The filter is sent to the SERVER rather than applied to the rows afterwards,
 * because `limit` is applied server-side too. Filtering after the fetch would
 * ask for three rows and then hide some of them, leaving a grid that is
 * silently short — "newest 3 published" has to be one question, not two.
 */
export function buildListParams(binding) {
  const params = {};
  if (binding.limit) {
    params.page_size = binding.limit;
  }
  if (binding.sort) {
    const descending = binding.sort.startsWith('-');
    params.sort = descending ? binding.sort.slice(1) : binding.sort;
    params.sort_dir = descending ? 'desc' : 'asc';
  }

  const filter = binding.filter;
  if (filter && filter.field && filter.op && filter.op !== 'any') {
    const values = filter.values || [];
    if (filter.op === 'in') {
      // An empty "is one of" is left off entirely: sending `filter[x__in]=`
      // would match nothing, which silently empties a grid the builder has
      // simply not finished configuring yet.
      if (values.length) params[`filter[${filter.field}__in]`] = values.join(',');
    } else if (values[0] !== undefined && values[0] !== '') {
      const key = filter.op === 'neq' ? `filter[${filter.field}__ne]` : `filter[${filter.field}]`;
      params[key] = values[0];
    }
  }

  return params;
}

/**
 * Assembles the two maps a section is handed, from bindings and the
 * react-query results for the subset of bindings that have a table_slug (in
 * the same order):
 *
 *   data   — { [div_id]: records[] }. A binding with no table_slug, a failed
 *            fetch, or an empty table all resolve to [] — never omitted,
 *            never thrown. UNCHANGED: every section already written against
 *            window.ARCEE_DATA keeps working exactly as before, which is why
 *            the error is reported beside the array and not inside it.
 *   errors — { [div_id]: { kind, status, requiresAuth, retryAfter } }, present
 *            ONLY for a binding that failed. An empty object means every
 *            binding answered.
 *
 * The two are separate because [] is genuinely ambiguous — it is "no rows"
 * and "we could not ask" at once — and a section that shows an empty state
 * has to tell those apart to say anything true. Sections that do not care
 * read `data` and never look at `errors`.
 */
export function buildArceeData(bindings, queryResults) {
  const data = {};
  const errors = {};
  let resultIndex = 0;
  bindings.forEach((binding) => {
    if (!binding.table_slug) {
      data[binding.div_id] = [];
      return;
    }
    const result = queryResults[resultIndex++];
    data[binding.div_id] = result?.isError ? [] : (result?.data?.records || result?.data?.results || []);
    if (result?.isError) {
      // const error = result.error || {};
      // errors[binding.div_id] = {
      //   kind: error.kind || 'server',
      //   status: error.status,
      //   requiresAuth: error.requiresAuth === true,
      //   retryAfter: error.retryAfter,
      // };
      // Moved into describeBindingError (unchanged) so a later query from the
      // section reports failures in exactly the same shape.
      errors[binding.div_id] = describeBindingError(result.error);
    }
  });
  return { data, errors };
}

/**
 * One failure, in the shape a section reads from ARCEE_ERRORS.
 *
 * `kind` is set by publicGet. A result that failed some other way — a
 * react-query cancellation, a throw from a custom queryFn in a test — is
 * reported as a server error rather than left undefined, so a consumer never
 * has to handle a fourth, nameless case.
 */
export function describeBindingError(error) {
  const e = error || {};
  return {
    kind: e.kind || 'server',
    status: e.status,
    requiresAuth: e.requiresAuth === true,
    retryAfter: e.retryAfter,
  };
}

/**
 * { [div_id]: { total, page, page_size, total_pages } } for every binding that
 * answered with one. Beside ARCEE_DATA rather than inside it, for the same
 * reason ARCEE_ERRORS is: every existing section reads ARCEE_DATA as plain
 * arrays, and that must not change.
 */
export function buildArceePagination(bindings, queryResults) {
  const out = {};
  let resultIndex = 0;
  bindings.forEach((binding) => {
    if (!binding.table_slug) return;
    const result = queryResults[resultIndex++];
    const pagination = !result?.isError && result?.data?.pagination;
    if (pagination) out[binding.div_id] = pagination;
  });
  return out;
}

/* ── Section queries (search, pages, order) ───────────────────────────────
 *
 * A binding is fetched once, before the section's script runs. A section that
 * pages or searches asks for MORE of the same binding with
 * `arceeQuery(divId, { page, search, … })`; the request is answered here, in
 * the parent, by the same fetch the binding used.
 *
 * The section can only choose from the keys below, and only for a div_id it
 * already binds — the TABLE always comes from the stored binding, never from
 * the message. So a section cannot read a table its configuration did not
 * name, and the server applies every rule it applies to the first fetch
 * (public access key, owner scoping, allowed search/sort fields, page cap).
 */

export const QUERY_MESSAGE_TYPE = 'ARCEE_QUERY';
export const QUERY_RESULT_TYPE = 'ARCEE_QUERY_RESULT';

const MAX_QUERY_TEXT = 200;
const SORT_DIRECTIONS = ['asc', 'desc', 'upcoming'];

/**
 * The binding's own params, with the section's request laid over them.
 * Anything not recognised, or not the right shape, is dropped rather than
 * forwarded — the result is always a request the binding could have made.
 */
export function buildQueryParams(binding, requested) {
  const params = buildListParams(binding);
  const r = requested && typeof requested === 'object' ? requested : {};

  const page = Number(r.page);
  if (Number.isInteger(page) && page >= 1) params.page = page;

  const size = Number(r.page_size);
  if (Number.isInteger(size) && size >= 1) params.page_size = size;   // the server caps it

  if (typeof r.search === 'string') {
    const search = r.search.trim().slice(0, MAX_QUERY_TEXT);
    if (search) params.search = search; else delete params.search;
  }

  if (typeof r.sort === 'string' && r.sort.trim()) {
    params.sort = r.sort.trim().slice(0, MAX_QUERY_TEXT);
    params.sort_dir = SORT_DIRECTIONS.includes(r.sort_dir) ? r.sort_dir : 'asc';
  }

  if (typeof r.today === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.today)) {
    params.today = r.today;
  }

  return params;
}

/**
 * The sandbox side: defines window.arceeQuery(divId, params) -> Promise of
 * { records, pagination, related }, rejecting with an ARCEE_ERRORS-shaped
 * object. Answers are matched by request id and accepted only from the parent
 * window. After each answer the frame re-reports its height (inline mode), as
 * the section has just redrawn with a different number of rows.
 */
export function buildQueryScript(instanceId) {
  return `;(function(){
  var IFRAME_ID = ${JSON.stringify(instanceId)};
  var seq = 0, pending = {};
  window.addEventListener('message', function (e) {
    var d = e.data;
    if (!d || d.type !== '${QUERY_RESULT_TYPE}' || e.source !== window.parent) return;
    var p = pending[d.reqId];
    if (!p) return;
    delete pending[d.reqId];
    if (d.ok) {
      p.resolve({ records: d.records || [], pagination: d.pagination || null, related: d.related || {} });
    } else {
      p.reject(d.error || { kind: 'server' });
    }
    var report = window.__arceeReportHeight;
    if (report) { setTimeout(report, 0); setTimeout(report, 300); }
  });
  window.arceeQuery = function (divId, params) {
    return new Promise(function (resolve, reject) {
      var reqId = IFRAME_ID + ':' + (++seq);
      pending[reqId] = { resolve: resolve, reject: reject };
      window.parent.postMessage({ type: '${QUERY_MESSAGE_TYPE}', id: IFRAME_ID, reqId: reqId,
                                  divId: divId, params: params || {} }, '*');
    });
  };
})();`;
}

/** JSON-serializes data for safe embedding inside a <script> tag in an HTML string. */
export function serializeArceeData(data) {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

/* ── Card buttons and badges ──────────────────────────────────────────────
 *
 * A section's card grid is built by the author's own JS inside the sandbox,
 * so the label on its button and the wording of its status badge used to be
 * written into that JS and CSS — retyped per page and invisible to anyone who
 * did not read the code. `section_config.card_buttons` moves both into stored
 * configuration, keyed by the SAME div_id the data binding uses so a section
 * binding two lists configures each separately.
 *
 * The two resolve* functions below are pure and are ALSO injected verbatim
 * into the sandbox (see HtmlCssSection), so the rule that decides whether a
 * button shows exists once rather than once here and once in author JS. That
 * is why they take everything as arguments and close over nothing — a
 * function that referenced module scope would break the moment it is
 * stringified into the iframe.
 */

/** Indexes card configs by div_id. A config with no div_id is unaddressable, so it is dropped. */
export function buildCardConfig(cardButtons) {
  const out = {};
  (cardButtons || []).forEach((entry) => {
    if (!entry || !entry.div_id) return;
    // `buttons` is the list; a lone `button` from an earlier save is read as a
    // list of one so existing sections keep rendering while they are migrated.
    var buttons = entry.buttons || (entry.button ? [entry.button] : []);
    out[entry.div_id] = { buttons: buttons, badge: entry.badge || null };
  });
  return out;
}

/**
 * Resolves the buttons to show on one row, in configured order.
 *
 * A LIST rather than one button because the same card wants different words in
 * different states — "Register" while registration is open, "View details"
 * once it is only published — and each of those is a button with its own rule
 * rather than one button with a second name.
 *
 * `visible_when` fails CLOSED for an unknown operator: a button shown because
 * nobody understood the rule is worse than one wrongly hidden — the same
 * stance the record-page action buttons take.
 */
export function resolveCardButtons(config, row) {
  var buttons = (config && config.buttons) || [];
  var out = [];

  for (var i = 0; i < buttons.length; i++) {
    var button = buttons[i];
    if (!button || !button.label) continue;

    var show = true;
    var rule = button.visible_when;
    if (rule && rule.field) {
      var value = row ? row[rule.field] : undefined;
      var values = rule.values || [];
      var op = rule.op || 'any';
      if (op === 'any') show = true;
      else if (op === 'in') show = values.indexOf(value) !== -1;
      else if (op === 'eq') show = values[0] === value;
      else if (op === 'neq') show = values[0] !== value;
      else show = false;
    }

    if (show) {
      out.push({ label: button.label, icon: button.icon || '', style: button.style || 'text' });
    }
  }

  return out;
}

/**
 * Resolves the badge for one row, or null when there is nothing to show.
 *
 * An unconfigured value yields null rather than the raw column value: showing
 * `REGISTRATION_OPEN` to a visitor is worse than showing nothing, and it is
 * how the CSS this replaces already behaved.
 */
export function resolveCardBadge(config, row) {
  var badge = config && config.badge;
  if (!badge || !badge.field) return null;

  var value = row ? row[badge.field] : undefined;
  if (value === undefined || value === null || value === '') return null;

  var labels = badge.labels || {};
  var label = labels[value];
  if (!label) return null;

  var colors = badge.colors || {};
  return { label: label, tone: colors[value] || 'muted' };
}

/**
 * The sandbox-side runtime: exposes arceeCardButton/arceeCardBadge to author JS.
 *
 * The resolvers are stringified in rather than reimplemented, so the rule that
 * decides whether a button shows has exactly one definition — the one the tests
 * above it cover.
 */
export function buildCardScript() {
  return `;(function(){
  var CARDS = window.ARCEE_CARDS || {};
  var resolveButtons = ${resolveCardButtons.toString()};
  var resolveBadge = ${resolveCardBadge.toString()};
  window.arceeCardButtons = function (divId, row) { return resolveButtons(CARDS[divId], row); };
  window.arceeCardBadge   = function (divId, row) { return resolveBadge(CARDS[divId], row); };
})();`;
}
